import { normalizeForMatch } from './textNormalize';
import { inputOnEnterHeaderFilter } from './inputOnEnterHeaderFilter';
import { primeMultiSelectHeaderFilter } from './primeMultiSelectHeaderFilter';
import { parseMaskFilters, parseSorters, safeParse, sanitizeTabulatorLayoutAttributes } from './tabulatorProgressiveLoading';
import { buildTabulatorIdColumn } from './tabulatorIdColumn';
import { buildAuditTabulatorColumns, TABULATOR_AUDIT_PROFILES } from './tabulatorAuditColumns';
import { tabulatorColumnMinWidthFromTitle } from './tabulatorColumnMinWidth';
import { uiT } from './uiTranslate';

export const CHECKLIST_TEMPLATE_EMPTY_FILTER_VALUE = '__empty__';

export const CHECKLIST_TEMPLATE_INTENSITY_VALUES = Object.freeze([
  'denne',
  'viackrat_den',
  'tyzdenne',
  'mesacne',
  'rocne',
  'nahodne',
]);

export const CHECKLIST_TEMPLATE_FORMAT_I18N_KEYS = Object.freeze({
  intensity: {
    denne: 'ChecklistTemplate.Intensity.denne',
    tyzdenne: 'ChecklistTemplate.Intensity.tyzdenne',
    mesacne: 'ChecklistTemplate.Intensity.mesacne',
    rocne: 'ChecklistTemplate.Intensity.rocne',
    viackrat_den: 'ChecklistTemplate.Intensity.viackrat_den',
    nahodne: 'ChecklistTemplate.Intensity.nahodne',
  },
  randomUntil: 'ChecklistTemplate.Intensity.random_until',
  weekdays: {
    1: 'ChecklistTemplate.Weekday.1',
    2: 'ChecklistTemplate.Weekday.2',
    3: 'ChecklistTemplate.Weekday.3',
    4: 'ChecklistTemplate.Weekday.4',
    5: 'ChecklistTemplate.Weekday.5',
    6: 'ChecklistTemplate.Weekday.6',
    7: 'ChecklistTemplate.Weekday.7',
  },
  specialDays: {
    first_monday: 'ChecklistTemplate.SpecialDay.first_monday',
    first_tuesday: 'ChecklistTemplate.SpecialDay.first_tuesday',
    first_wednesday: 'ChecklistTemplate.SpecialDay.first_wednesday',
    first_thursday: 'ChecklistTemplate.SpecialDay.first_thursday',
    first_friday: 'ChecklistTemplate.SpecialDay.first_friday',
    last_monday: 'ChecklistTemplate.SpecialDay.last_monday',
    last_tuesday: 'ChecklistTemplate.SpecialDay.last_tuesday',
    last_wednesday: 'ChecklistTemplate.SpecialDay.last_wednesday',
    last_thursday: 'ChecklistTemplate.SpecialDay.last_thursday',
    last_friday: 'ChecklistTemplate.SpecialDay.last_friday',
  },
  notif: {
    '1 hours': 'ChecklistTemplate.Notif.1_hours',
    '2 hours': 'ChecklistTemplate.Notif.2_hours',
    '4 hours': 'ChecklistTemplate.Notif.4_hours',
    '6 hours': 'ChecklistTemplate.Notif.6_hours',
    '1 days': 'ChecklistTemplate.Notif.1_days',
    '2 days': 'ChecklistTemplate.Notif.2_days',
    '3 days': 'ChecklistTemplate.Notif.3_days',
    '5 days': 'ChecklistTemplate.Notif.5_days',
    '7 days': 'ChecklistTemplate.Notif.7_days',
    '10 days': 'ChecklistTemplate.Notif.10_days',
    '14 days': 'ChecklistTemplate.Notif.14_days',
    '30 days': 'ChecklistTemplate.Notif.30_days',
  },
});

export function defaultChecklistTemplateFormatLabels() {
  return {
    intensity: {
      denne: uiT('ChecklistTemplate.Intensity.denne'),
      tyzdenne: uiT('ChecklistTemplate.Intensity.tyzdenne'),
      mesacne: uiT('ChecklistTemplate.Intensity.mesacne'),
      rocne: uiT('ChecklistTemplate.Intensity.rocne'),
      viackrat_den: uiT('ChecklistTemplate.Intensity.viackrat_den'),
      nahodne: uiT('ChecklistTemplate.Intensity.nahodne'),
    },
    weekdays: {
      1: uiT('ChecklistTemplate.Weekday.1'),
      2: uiT('ChecklistTemplate.Weekday.2'),
      3: uiT('ChecklistTemplate.Weekday.3'),
      4: uiT('ChecklistTemplate.Weekday.4'),
      5: uiT('ChecklistTemplate.Weekday.5'),
      6: uiT('ChecklistTemplate.Weekday.6'),
      7: uiT('ChecklistTemplate.Weekday.7'),
    },
    special_days: {
      first_monday: uiT('ChecklistTemplate.SpecialDay.first_monday'),
      first_tuesday: uiT('ChecklistTemplate.SpecialDay.first_tuesday'),
      first_wednesday: uiT('ChecklistTemplate.SpecialDay.first_wednesday'),
      first_thursday: uiT('ChecklistTemplate.SpecialDay.first_thursday'),
      first_friday: uiT('ChecklistTemplate.SpecialDay.first_friday'),
      last_monday: uiT('ChecklistTemplate.SpecialDay.last_monday'),
      last_tuesday: uiT('ChecklistTemplate.SpecialDay.last_tuesday'),
      last_wednesday: uiT('ChecklistTemplate.SpecialDay.last_wednesday'),
      last_thursday: uiT('ChecklistTemplate.SpecialDay.last_thursday'),
      last_friday: uiT('ChecklistTemplate.SpecialDay.last_friday'),
    },
    random_until: uiT('ChecklistTemplate.Intensity.random_until'),
    notif: {
      '1 hours': uiT('ChecklistTemplate.Notif.1_hours'),
      '2 hours': uiT('ChecklistTemplate.Notif.2_hours'),
      '4 hours': uiT('ChecklistTemplate.Notif.4_hours'),
      '6 hours': uiT('ChecklistTemplate.Notif.6_hours'),
      '1 days': uiT('ChecklistTemplate.Notif.1_days'),
      '2 days': uiT('ChecklistTemplate.Notif.2_days'),
      '3 days': uiT('ChecklistTemplate.Notif.3_days'),
      '5 days': uiT('ChecklistTemplate.Notif.5_days'),
      '7 days': uiT('ChecklistTemplate.Notif.7_days'),
      '10 days': uiT('ChecklistTemplate.Notif.10_days'),
      '14 days': uiT('ChecklistTemplate.Notif.14_days'),
      '30 days': uiT('ChecklistTemplate.Notif.30_days'),
    },
  };
}

function pickFormatLabel(t, key, fallback) {
  if (typeof t !== 'function') return fallback;
  const value = t(key);
  if (value == null || value === '' || value === key) return fallback;
  return String(value);
}

export function resolveChecklistTemplateFormatLabels(t) {
  const defaults = defaultChecklistTemplateFormatLabels();
  const intensity = {};
  Object.entries(CHECKLIST_TEMPLATE_FORMAT_I18N_KEYS.intensity).forEach(([key, i18nKey]) => {
    intensity[key] = pickFormatLabel(t, i18nKey, defaults.intensity[key]);
  });
  const weekdays = {};
  Object.entries(CHECKLIST_TEMPLATE_FORMAT_I18N_KEYS.weekdays).forEach(([key, i18nKey]) => {
    weekdays[Number(key)] = pickFormatLabel(t, i18nKey, defaults.weekdays[Number(key)]);
  });
  const specialDays = {};
  Object.entries(CHECKLIST_TEMPLATE_FORMAT_I18N_KEYS.specialDays).forEach(([key, i18nKey]) => {
    specialDays[key] = pickFormatLabel(t, i18nKey, defaults.special_days[key]);
  });
  const notif = {};
  Object.entries(CHECKLIST_TEMPLATE_FORMAT_I18N_KEYS.notif).forEach(([key, i18nKey]) => {
    notif[key] = pickFormatLabel(t, i18nKey, defaults.notif[key]);
  });
  return {
    intensity,
    weekdays,
    special_days: specialDays,
    random_until: pickFormatLabel(t, CHECKLIST_TEMPLATE_FORMAT_I18N_KEYS.randomUntil, defaults.random_until),
    notif,
  };
}

function resolveFormatLabels(labels) {
  return labels?.intensity ? labels : defaultChecklistTemplateFormatLabels();
}

export const CHECKLIST_TEMPLATE_IS_ACTIVE_OPTIONS = Object.freeze([
  { get label() { return uiT('Common.Active'); }, value: '1' },
  { get label() { return uiT('Common.Inactive'); }, value: '0' },
]);

export const CHECKLIST_TEMPLATE_SETTINGS = Object.freeze({
  maskPage: 'ChecklistTemplateList',
  getAllUrl: '/api/checklist_templates',
  dataStateKey: 'checklist_templates',
  addPage: 'ChecklistTemplateAdd',
  tabMask: 'chck001',
  listTabMask: 'chck002',
});

export const DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  name: true,
  department_ids: true,
  users: true,
  sending_data: true,
  notification_before: true,
  manager_id: true,
  manager_due_days: true,
  description: true,
  'machine.name': true,
  is_active: true,
  created_at: true,
  updated_at: true,
});

export const CHECKLIST_TEMPLATE_CANONICAL_FIELDS = Object.freeze(
  Object.keys(DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY),
);

export const CHECKLIST_TEMPLATE_TECHNICAL_FIELDS = Object.freeze(['rowSelection', 'editor']);
export const CHECKLIST_TEMPLATE_FROZEN_LEFT_FIELDS = Object.freeze(['rowSelection', 'editor', 'id']);
export const CHECKLIST_TEMPLATE_AUDIT_FIELDS = Object.freeze(['created_at', 'updated_at']);
export const CHECKLIST_TEMPLATE_EXPORT_FIELDS = Object.freeze([
  'id',
  'name',
  'department_ids',
  'users',
  'sending_data',
  'notification_before',
  'manager_id',
  'manager_due_days',
  'description',
  'machine.name',
  'is_active',
  'created_at',
  'updated_at',
]);

export const CHECKLIST_TEMPLATE_LEGACY_FIELD_ALIASES = Object.freeze({
  applicable_types: null,
  custom_department: 'department_ids',
  manager_name: 'manager_id',
  cusom_notif_before: 'notification_before',
});

export const CHECKLIST_TEMPLATE_NOTIF_BEFORE_OPTIONS = Object.freeze([
  { get label() { return uiT('ChecklistTemplate.Notif.1_hours'); }, value: '1 hours' },
  { get label() { return uiT('ChecklistTemplate.Notif.2_hours'); }, value: '2 hours' },
  { get label() { return uiT('ChecklistTemplate.Notif.4_hours'); }, value: '4 hours' },
  { get label() { return uiT('ChecklistTemplate.Notif.6_hours'); }, value: '6 hours' },
  { get label() { return uiT('ChecklistTemplate.Notif.1_days'); }, value: '1 days' },
  { get label() { return uiT('ChecklistTemplate.Notif.2_days'); }, value: '2 days' },
  { get label() { return uiT('ChecklistTemplate.Notif.3_days'); }, value: '3 days' },
  { get label() { return uiT('ChecklistTemplate.Notif.5_days'); }, value: '5 days' },
  { get label() { return uiT('ChecklistTemplate.Notif.7_days'); }, value: '7 days' },
  { get label() { return uiT('ChecklistTemplate.Notif.10_days'); }, value: '10 days' },
  { get label() { return uiT('ChecklistTemplate.Notif.14_days'); }, value: '14 days' },
  { get label() { return uiT('ChecklistTemplate.Notif.30_days'); }, value: '30 days' },
]);

export const CHECKLIST_TEMPLATE_COLUMN_TITLES = Object.freeze({
  get id() { return 'ID'; },
  get name() { return uiT('Common.Name'); },
  get department_ids() { return uiT('ChecklistTemplate.Column.Department'); },
  get users() { return uiT('ChecklistTemplate.Column.Employee'); },
  get sending_data() { return uiT('ChecklistTemplate.Column.Intensity'); },
  get notification_before() { return uiT('Common.Notification'); },
  get manager_id() { return uiT('ChecklistTemplate.Column.Manager'); },
  get manager_due_days() { return uiT('ChecklistTemplate.Column.ManagerDueDays'); },
  get description() { return uiT('Common.Description'); },
  get 'machine.name'() { return uiT('Common.Machine'); },
  get is_active() { return uiT('ChecklistTemplate.Column.ActivityStatus'); },
  get created_at() { return uiT('Common.Column.CreatedAt'); },
  get updated_at() { return uiT('Common.Column.UpdatedAt'); },
});

const CANONICAL_SET = new Set(CHECKLIST_TEMPLATE_CANONICAL_FIELDS);
const TECH_SET = new Set(CHECKLIST_TEMPLATE_TECHNICAL_FIELDS);
const FROZEN_LEFT_SET = new Set(CHECKLIST_TEMPLATE_FROZEN_LEFT_FIELDS);
const AUDIT_SET = new Set(CHECKLIST_TEMPLATE_AUDIT_FIELDS);

export function normalizeChecklistTemplateText(value) {
  return normalizeForMatch(value);
}

export function normalizeChecklistTemplateId(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  return s === '' || s === 'null' || s === 'undefined' ? null : s;
}

export function canonicalizeChecklistTemplateField(field, { allowTechnical = false } = {}) {
  if (field == null || field === '') return null;
  const raw = String(field);
  if (allowTechnical && TECH_SET.has(raw)) return raw;
  if (CANONICAL_SET.has(raw)) return raw;
  if (Object.prototype.hasOwnProperty.call(CHECKLIST_TEMPLATE_LEGACY_FIELD_ALIASES, raw)) {
    return CHECKLIST_TEMPLATE_LEGACY_FIELD_ALIASES[raw];
  }
  return null;
}

export function migrateLegacyChecklistTemplateColumns(saved) {
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};
  const out = {};
  for (const field of CHECKLIST_TEMPLATE_CANONICAL_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(saved, field)) {
      out[field] = saved[field] !== false;
    }
  }
  for (const [legacy, canonical] of Object.entries(CHECKLIST_TEMPLATE_LEGACY_FIELD_ALIASES)) {
    if (!canonical) continue;
    if (Object.prototype.hasOwnProperty.call(out, canonical)) continue;
    if (Object.prototype.hasOwnProperty.call(saved, legacy)) {
      out[canonical] = saved[legacy] !== false;
    }
  }
  return out;
}

export function mergeChecklistTemplateColumnVisibility(saved) {
  const migrated = migrateLegacyChecklistTemplateColumns(saved);
  const merged = { ...DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY };
  for (const field of CHECKLIST_TEMPLATE_CANONICAL_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(migrated, field)) {
      merged[field] = migrated[field] !== false;
    }
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY };
  return merged;
}

export function migrateLegacyChecklistTemplateFilters(filters) {
  const list = Array.isArray(filters) ? filters : [];
  return list
    .map((f) => {
      const raw = typeof f?.field === 'string' ? f.field : typeof f?.column === 'string' ? f.column : null;
      const field = canonicalizeChecklistTemplateField(raw);
      if (!field) return null;
      return { ...f, field };
    })
    .filter(Boolean);
}

export function migrateLegacyChecklistTemplateSorters(sorters) {
  const list = Array.isArray(sorters) ? sorters : [];
  return list
    .map((s) => {
      const raw = typeof s?.field === 'string' ? s.field : typeof s?.column === 'string' ? s.column : null;
      const field = canonicalizeChecklistTemplateField(raw);
      if (!field) return null;
      return { ...s, field, column: field };
    })
    .filter(Boolean);
}

function migrateLayoutNode(node) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return null;
  const cloned = { ...node };
  if (typeof cloned.field === 'string' && cloned.field !== '') {
    const canonical = canonicalizeChecklistTemplateField(cloned.field, { allowTechnical: true });
    if (!canonical) return null;
    cloned.field = canonical;
  }
  if (Array.isArray(cloned.columns)) {
    cloned.columns = cloned.columns.map(migrateLayoutNode).filter(Boolean);
  }
  return cloned;
}

export function migrateLegacyChecklistTemplateAttributes(attributes) {
  if (!Array.isArray(attributes)) return [];
  return attributes.map(migrateLayoutNode).filter(Boolean);
}

export function migrateChecklistTemplateMaskRow(maskRow) {
  if (!maskRow) return maskRow;
  return {
    ...maskRow,
    columns: migrateLegacyChecklistTemplateColumns(safeParse(maskRow.columns, {})),
    filters: migrateLegacyChecklistTemplateFilters(parseMaskFilters(maskRow.filters)),
    sorters: migrateLegacyChecklistTemplateSorters(parseSorters(maskRow.sorters)),
    attributes: migrateLegacyChecklistTemplateAttributes(safeParse(maskRow.attributes, [])),
  };
}

function pushSafeLayoutNode(out, seen, node) {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node.columns)) {
    node.columns.forEach((child) => pushSafeLayoutNode(out, seen, child));
  }
  if (typeof node.field !== 'string' || node.field === '') return;
  const field = canonicalizeChecklistTemplateField(node.field, { allowTechnical: true });
  if (!field || seen.has(field)) return;
  seen.add(field);
  const safe = { field };
  if (field !== 'id' && typeof node.width === 'number' && Number.isFinite(node.width)) safe.width = node.width;
  if (node.visible === false) safe.visible = false;
  else if (node.visible === true) safe.visible = true;
  out.push(safe);
}

export function toSafeChecklistTemplateLayoutAttributes(attributes) {
  const migrated = migrateLegacyChecklistTemplateAttributes(attributes);
  const sanitized = sanitizeTabulatorLayoutAttributes(migrated);
  const out = [];
  const seen = new Set();
  (Array.isArray(sanitized) ? sanitized : []).forEach((node) => pushSafeLayoutNode(out, seen, node));
  for (const field of CHECKLIST_TEMPLATE_CANONICAL_FIELDS) {
    if (seen.has(field)) continue;
    seen.add(field);
    out.push({ field, visible: DEFAULT_CHECKLIST_TEMPLATE_COLUMN_VISIBILITY[field] !== false });
  }
  return out;
}

function forceFrozenRuntimeColumn(col) {
  if (!col?.field) return col;
  if (FROZEN_LEFT_SET.has(col.field)) {
    return { ...col, frozen: true };
  }
  if (AUDIT_SET.has(col.field)) {
    const next = { ...col };
    delete next.frozen;
    return next;
  }
  if (Object.prototype.hasOwnProperty.call(col, 'frozen')) {
    const next = { ...col };
    delete next.frozen;
    return next;
  }
  return col;
}

export function applyMaskToCanonicalChecklistTemplateColumns(canonicalColumns, maskRow) {
  const byField = new Map();
  (canonicalColumns || []).forEach((col) => {
    if (col?.field) byField.set(col.field, col);
  });
  const migrated = maskRow ? migrateChecklistTemplateMaskRow(maskRow) : null;
  const visibility = mergeChecklistTemplateColumnVisibility(migrated?.columns);
  const layout = toSafeChecklistTemplateLayoutAttributes(migrated?.attributes);
  const used = new Set();
  const ordered = [];
  for (const node of layout) {
    if (!CANONICAL_SET.has(node.field) || used.has(node.field) || FROZEN_LEFT_SET.has(node.field)) continue;
    const col = byField.get(node.field);
    if (!col) continue;
    used.add(node.field);
    const next = { ...col };
    if (node.width != null && node.field !== 'id') next.width = node.width;
    next.visible = visibility[node.field] !== false;
    ordered.push(forceFrozenRuntimeColumn(next));
  }
  for (const field of CHECKLIST_TEMPLATE_CANONICAL_FIELDS) {
    if (used.has(field) || FROZEN_LEFT_SET.has(field)) continue;
    const col = byField.get(field);
    if (!col) continue;
    ordered.push(forceFrozenRuntimeColumn({ ...col, visible: visibility[field] !== false }));
  }
  const frozenLeft = CHECKLIST_TEMPLATE_FROZEN_LEFT_FIELDS.map((field) => {
    const col = byField.get(field);
    if (!col) return null;
    const next = { ...col, frozen: true };
    if (field === 'id') next.visible = visibility.id !== false;
    return next;
  }).filter(Boolean);
  return { columns: [...frozenLeft, ...ordered], visibility, layout };
}

export function columnHasHeaderFilter(def) {
  if (!def) return false;
  return def.headerFilter != null && def.headerFilter !== false && def.headerFilter !== '';
}

export function getRuntimeColumnDefinition(table, field) {
  if (!table || field == null) return null;
  try {
    const col = table.getColumn?.(field);
    if (!col) return null;
    if (typeof col.getDefinition === 'function') return col.getDefinition() || null;
    return col.definition || null;
  } catch (_) {
    return null;
  }
}

export function restoreChecklistTemplateHeaderFilters(table, maskRow, columnDefs = []) {
  if (!table) return [];
  const migrated = maskRow ? migrateChecklistTemplateMaskRow(maskRow) : null;
  const filters = Array.isArray(migrated?.filters) ? migrated.filters : [];
  const restored = [];
  filters.forEach((filter) => {
    const field = canonicalizeChecklistTemplateField(filter?.field);
    if (!field || !CANONICAL_SET.has(field)) return;
    const def = (columnDefs || []).find((col) => col?.field === field);
    if (!columnHasHeaderFilter(def)) return;
    const runtime = getRuntimeColumnDefinition(table, field);
    if (!runtime || !columnHasHeaderFilter(runtime)) return;
    try {
      table.setHeaderFilterValue?.(field, filter.value ?? '', true);
      restored.push(field);
    } catch (_) {}
  });
  return restored;
}

export function restoreChecklistTemplateSorters(table, maskRow, columnDefs = []) {
  if (!table) return [];
  const migrated = maskRow ? migrateChecklistTemplateMaskRow(maskRow) : null;
  const sorters = Array.isArray(migrated?.sorters) ? migrated.sorters : [];
  const next = [];
  sorters.forEach((sorter) => {
    const field = canonicalizeChecklistTemplateField(sorter?.field || sorter?.column);
    if (!field || !CANONICAL_SET.has(field)) return;
    const def = (columnDefs || []).find((col) => col?.field === field);
    if (!def) return;
    const runtime = getRuntimeColumnDefinition(table, field);
    if (!runtime) return;
    next.push({ column: field, dir: sorter?.dir === 'desc' ? 'desc' : 'asc' });
  });
  if (next.length) {
    try { table.setSort?.(next); } catch (_) {}
  }
  return next;
}

export function applySafeChecklistTemplateLayoutToTable(table, maskRow) {
  if (!table) return { visibility: mergeChecklistTemplateColumnVisibility({}), layout: [] };
  const migrated = maskRow ? migrateChecklistTemplateMaskRow(maskRow) : null;
  const visibility = mergeChecklistTemplateColumnVisibility(migrated?.columns);
  const layout = toSafeChecklistTemplateLayoutAttributes(migrated?.attributes);
  for (const node of layout) {
    if (!CANONICAL_SET.has(node.field) || node.width == null) continue;
    try {
      table.getColumn?.(node.field)?.setWidth?.(node.width);
    } catch (_) {}
  }
  for (const field of CHECKLIST_TEMPLATE_CANONICAL_FIELDS) {
    try {
      if (visibility[field] === false) table.hideColumn?.(field);
      else table.showColumn?.(field);
    } catch (_) {}
  }
  const dataOrder = layout.map((node) => node.field).filter((field) => CANONICAL_SET.has(field));
  for (let i = 1; i < dataOrder.length; i += 1) {
    try {
      table.moveColumn?.(dataOrder[i], dataOrder[i - 1], true);
    } catch (_) {}
  }
  try {
    table.redraw?.(true);
  } catch (_) {}
  return { visibility, layout };
}

export function parseSelectedFilterValues(headerValue) {
  if (headerValue == null || headerValue === '') return [];
  if (Array.isArray(headerValue)) {
    return headerValue.map(normalizeChecklistTemplateId).filter(Boolean);
  }
  return String(headerValue)
    .split(',')
    .map((part) => normalizeChecklistTemplateId(part))
    .filter(Boolean);
}

export function matchesIdMultiSelect(headerValue, ids) {
  const selected = parseSelectedFilterValues(headerValue);
  if (selected.length === 0) return true;
  const normalizedIds = (Array.isArray(ids) ? ids : []).map(normalizeChecklistTemplateId).filter(Boolean);
  const isEmpty = normalizedIds.length === 0;
  if (selected.includes(CHECKLIST_TEMPLATE_EMPTY_FILTER_VALUE) && isEmpty) return true;
  return normalizedIds.some((id) => selected.includes(id));
}

export function departmentIdsFromTemplateRow(rowData) {
  return (rowData?.users || [])
    .map((user) => user?.employee?.department_id ?? user?.employee?.department?.id)
    .filter((id) => normalizeChecklistTemplateId(id) != null);
}

export function userIdsFromTemplateRow(rowData) {
  return (rowData?.users || []).map((user) => user?.id).filter((id) => normalizeChecklistTemplateId(id) != null);
}

export function managerIdFromTemplateRow(rowData) {
  return rowData?.manager_id ?? rowData?.manager?.id ?? null;
}

export function machineNameFromTemplateRow(rowData) {
  const name = rowData?.machine?.name;
  return name == null ? '' : String(name);
}

export function departmentHeaderFilter(headerValue, _rowValue, rowData) {
  return matchesIdMultiSelect(headerValue, departmentIdsFromTemplateRow(rowData));
}

export function usersHeaderFilter(headerValue, _rowValue, rowData) {
  return matchesIdMultiSelect(headerValue, userIdsFromTemplateRow(rowData));
}

export function managerHeaderFilter(headerValue, _rowValue, rowData) {
  const id = managerIdFromTemplateRow(rowData);
  return matchesIdMultiSelect(headerValue, id == null || id === '' ? [] : [id]);
}

export function machineNameHeaderFilter(headerValue, _rowValue, rowData) {
  const name = machineNameFromTemplateRow(rowData);
  return matchesIdMultiSelect(headerValue, name === '' ? [] : [name]);
}

export function textHeaderFilter(headerValue, rowValue, rowData, filterParams) {
  if (headerValue == null || String(headerValue).trim() === '') return true;
  const field = filterParams?.field;
  const source = field && rowData && Object.prototype.hasOwnProperty.call(rowData, field)
    ? rowData[field]
    : rowValue;
  if (source == null || source === '') return false;
  return normalizeChecklistTemplateText(source).includes(normalizeChecklistTemplateText(headerValue));
}

export function numberHeaderFilter(headerValue, rowValue, rowData) {
  if (headerValue === undefined || headerValue === null || String(headerValue).trim() === '') return true;
  const raw = rowData?.manager_due_days ?? rowValue;
  const needle = String(headerValue).trim();
  const asNumber = Number(needle);
  if (!Number.isNaN(asNumber) && needle !== '') {
    return Number(raw) === asNumber;
  }
  if (raw == null || raw === '') return false;
  return normalizeChecklistTemplateText(raw).includes(normalizeChecklistTemplateText(needle));
}

export function isActiveHeaderFilter(headerValue, rowValue, rowData) {
  if (headerValue == null || headerValue === '') return true;
  const selected = parseSelectedFilterValues(headerValue);
  if (selected.length === 0) return true;
  const raw = rowData?.is_active ?? rowValue;
  if (raw == null || raw === '') {
    return selected.includes(CHECKLIST_TEMPLATE_EMPTY_FILTER_VALUE);
  }
  const isActive = raw === true || raw === 'true' || Number(raw) === 1 || String(raw) === '1';
  return selected.some((token) => {
    const normalized = normalizeChecklistTemplateText(token);
    if (token === CHECKLIST_TEMPLATE_EMPTY_FILTER_VALUE) return false;
    if (normalized === '1' || normalized === 'aktivny' || normalized === 'true') return isActive;
    if (normalized === '0' || normalized === 'neaktivny' || normalized === 'false') return !isActive;
    return false;
  });
}

export function parseSendingData(value) {
  if (value == null || value === '') return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function getNotifBeforeLabel(value, labels) {
  const key = String(value ?? '').trim();
  const resolved = resolveFormatLabels(labels);
  if (resolved.notif?.[key]) return resolved.notif[key];
  const option = CHECKLIST_TEMPLATE_NOTIF_BEFORE_OPTIONS.find((opt) => opt.value === key);
  return option ? option.label : key;
}

export function formatNotificationBefore(sendingData, labels) {
  const parsed = parseSendingData(sendingData);
  const raw = parsed?.notification_before;
  if (raw == null || raw === '') return '';
  const values = Array.isArray(raw) ? raw : [raw];
  return values
    .map((item) => getNotifBeforeLabel(item, labels))
    .filter((label) => label != null && String(label).trim() !== '')
    .join('/');
}

export function notificationBeforeHeaderFilter(headerValue, rowValue, rowData, filterParams) {
  if (headerValue == null || String(headerValue).trim() === '') return true;
  const sending = rowData?.sending_data ?? rowValue;
  const parsed = parseSendingData(sending);
  const raw = parsed?.notification_before;
  if (raw == null || raw === '') return false;
  const values = Array.isArray(raw) ? raw : [raw];
  const formatted = formatNotificationBefore(sending, filterParams?.labels);
  const haystack = normalizeChecklistTemplateText(
    [...values, formatted].filter((part) => part != null && String(part).trim() !== '').join(' '),
  );
  return haystack.includes(normalizeChecklistTemplateText(headerValue));
}

export function formatIntensityText(value, labels) {
  if (!value) return '';
  let data;
  try {
    data = typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return '';
  }
  if (!data || typeof data !== 'object') return '';

  const resolved = resolveFormatLabels(labels);
  const intensity = data.intensity || '';
  const time = data.time || '';
  const name = resolved.intensity[intensity] ?? intensity;
  const joinList = (arr) =>
    (Array.isArray(arr) ? arr : [arr])
      .filter((v) => v !== undefined && v !== null && String(v).trim() !== '')
      .map((v) => String(v).trim())
      .join(',');

  switch (intensity) {
    case 'denne':
      return `${name}/${time}`;
    case 'tyzdenne': {
      const days = Array.isArray(data.daysOfWeek) ? data.daysOfWeek : [];
      const daysStr = days.map((d) => resolved.weekdays[Number(d)] || String(d)).join(',');
      return `${name}/${daysStr}/${time}`;
    }
    case 'mesacne': {
      if (data.dayMonth && typeof data.dayMonth === 'string') {
        const label = resolved.special_days[data.dayMonth] || data.dayMonth;
        return `${name}/${label}/${time}`;
      }
      if (Array.isArray(data.daysOfMonth) && data.daysOfMonth.length) {
        const daysStr = data.daysOfMonth.map((d) => Number(d)).join(',');
        return `${name}/${daysStr}/${time}`;
      }
      if (data.dayMonth && typeof data.dayMonth === 'object') {
        const d = Number(data.dayMonth.day || '');
        return Number.isFinite(d) ? `${name}/${d}/${time}` : `${name}/${time}`;
      }
      return `${name}/${time}`;
    }
    case 'rocne': {
      const dm = data.dayMonth || {};
      const day = Number(dm.day || data.day || '');
      const month = Number(dm.month || data.month || '');
      const dayStr = Number.isFinite(day) ? String(day) : '';
      const monthStr = Number.isFinite(month) ? String(month) : '';
      const datePart = dayStr && monthStr ? `${dayStr}.${monthStr}` : `${dayStr || monthStr}`;
      return `${name}/${datePart}/${time}`;
    }
    case 'viackrat_den': {
      const times = joinList(data.times || data.timesDaily || []);
      return `${name}/${times}`;
    }
    case 'nahodne':
      return `${name}/${resolved.random_until ?? 'do '}${time}`;
    default:
      return intensity || '';
  }
}

export function intensityHeaderFilter(headerValue, rowValue, rowData, filterParams) {
  if (headerValue == null || String(headerValue).trim() === '') return true;
  const sending = rowData?.sending_data ?? rowValue;
  const formatted = formatIntensityText(sending, filterParams?.labels);
  if (!formatted) return false;
  return normalizeChecklistTemplateText(formatted).includes(normalizeChecklistTemplateText(headerValue));
}

export function departmentNamesFromTemplateRow(rowData) {
  const names = [];
  const seen = new Set();
  (rowData?.users || []).forEach((user) => {
    const name = user?.employee?.department?.name;
    if (!name) return;
    const key = String(name);
    if (seen.has(key)) return;
    seen.add(key);
    names.push(key);
  });
  return names;
}

export function userNamesFromTemplateRow(rowData) {
  return (rowData?.users || []).map((user) => user?.employee?.name || user?.username || '').filter(Boolean);
}

export function managerNameFromTemplateRow(rowData) {
  return rowData?.manager?.employee?.name || rowData?.manager?.username || '';
}

export function isActiveLabel(rowData, labels = {}) {
  const active = labels.active || uiT('Common.Active');
  const inactive = labels.inactive || uiT('Common.Inactive');
  return Number(rowData?.is_active) === 1 ? active : inactive;
}

export function buildChecklistTemplateSearchHaystack(rowData, labels = {}) {
  const createdUser = rowData?.created_by_user || rowData?.createdByUser;
  const updatedUser = rowData?.updated_by_user || rowData?.updatedByUser;
  const parts = [
    rowData?.id,
    rowData?.name,
    rowData?.description,
    rowData?.manager_due_days,
    ...departmentNamesFromTemplateRow(rowData),
    ...userNamesFromTemplateRow(rowData),
    formatIntensityText(rowData?.sending_data, labels),
    formatNotificationBefore(rowData?.sending_data, labels),
    managerNameFromTemplateRow(rowData),
    machineNameFromTemplateRow(rowData),
    isActiveLabel(rowData, labels),
    rowData?.created_at,
    rowData?.updated_at,
    createdUser?.employee?.name,
    createdUser?.username,
    updatedUser?.employee?.name,
    updatedUser?.username,
  ];
  return normalizeChecklistTemplateText(parts.filter((part) => part != null && String(part).trim() !== '').join(' '));
}

export function checklistTemplateSearchFilter(rowData, searchValue, labels = {}) {
  const needle = normalizeChecklistTemplateText(searchValue);
  if (!needle) return true;
  return buildChecklistTemplateSearchHaystack(rowData, labels).includes(needle);
}

export function buildSortedSelectOptions(items, { getLabel, getValue, emptyLabel } = {}) {
  const emptyOption = emptyLabel
    ? { label: emptyLabel, value: CHECKLIST_TEMPLATE_EMPTY_FILTER_VALUE }
    : null;
  const mapped = (Array.isArray(items) ? items : [])
    .map((item) => {
      const value = getValue ? getValue(item) : item?.id;
      const normalized = normalizeChecklistTemplateId(value);
      if (normalized == null) return null;
      const label = getLabel ? getLabel(item) : String(item?.name ?? normalized);
      if (!label || String(label).trim() === '') return null;
      return { label: String(label), value: normalized };
    })
    .filter(Boolean)
    .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'sk'));
  return emptyOption ? [emptyOption, ...mapped] : mapped;
}

function defaultDepartmentFormatter(cell) {
  return departmentNamesFromTemplateRow(cell.getRow()?.getData?.() || {}).join(', ');
}

function defaultUserFormatter(cell) {
  return userNamesFromTemplateRow(cell.getRow()?.getData?.() || {}).join(', ');
}

function defaultIntensityFormatter(cell, labels) {
  const row = cell.getRow?.()?.getData?.() || {};
  return formatIntensityText(row.sending_data ?? cell.getValue?.(), labels);
}

function defaultNotificationFormatter(cell, labels) {
  const row = cell.getRow?.()?.getData?.() || {};
  return formatNotificationBefore(row.sending_data, labels);
}

function defaultManagerFormatter(cell) {
  return managerNameFromTemplateRow(cell.getRow()?.getData?.() || {});
}

function defaultMachineFormatter(cell) {
  const name = machineNameFromTemplateRow(cell.getRow()?.getData?.() || {});
  return name || '–';
}

function defaultActiveFormatter(cell) {
  return isActiveLabel(cell.getRow?.()?.getData?.() || { is_active: cell.getValue?.() });
}

function idHeaderFilter(headerValue, rowValue, rowData) {
  if (headerValue == null || String(headerValue).trim() === '') return true;
  return String(rowData?.id ?? rowValue ?? '').includes(String(headerValue).trim());
}

export function canonicalizeChecklistTemplateExportField(field) {
  const canonical = canonicalizeChecklistTemplateField(field);
  if (!canonical) return null;
  return CHECKLIST_TEMPLATE_EXPORT_FIELDS.includes(canonical) ? canonical : null;
}

export function serializeChecklistTemplateExportFilterValue(value) {
  const selected = parseSelectedFilterValues(value);
  if (selected.length > 0) return selected.join(',');
  if (value == null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}

export function buildChecklistTemplateExportFilterParams(headerFilters) {
  const out = {};
  (Array.isArray(headerFilters) ? headerFilters : []).forEach((filter) => {
    const field = canonicalizeChecklistTemplateExportField(filter?.field);
    if (!field) return;
    const serialized = serializeChecklistTemplateExportFilterValue(filter?.value);
    if (serialized == null) return;
    out[`filter_${field}`] = serialized;
  });
  return out;
}

export function visibleChecklistTemplateExportColumns(columns, visibilityMap) {
  const allowed = new Set(CHECKLIST_TEMPLATE_EXPORT_FIELDS);
  const seen = new Set();
  const out = [];
  (Array.isArray(columns) ? columns : []).forEach((col) => {
    const field = col?.field || col?.getField?.() || col?.getDefinition?.()?.field;
    if (!field || !allowed.has(field) || seen.has(field)) return;
    if (visibilityMap?.[field] === false) return;
    if (col?.visible === false) return;
    seen.add(field);
    out.push(field);
  });
  return out.length > 0
    ? out
    : CHECKLIST_TEMPLATE_EXPORT_FIELDS.filter((field) => visibilityMap?.[field] !== false);
}

export function buildChecklistTemplateColumns({
  departmentOptions = [],
  userOptions = [],
  managerOptions = [],
  machineOptions = [],
  departmentFormatter,
  userFormatter,
  intensityFormatter,
  notificationFormatter,
  managerFormatter,
  machineFormatter,
  activeFormatter,
  editRowFormatter,
  dateFormat = 'dd.MM.yyyy',
  dateTimeFormat,
  getUserColor,
  getUserInitials,
  formatLabels,
} = {}) {
  const dateTimeFmt = dateTimeFormat || `${dateFormat} HH:mm`;
  const labels = resolveFormatLabels(formatLabels);
  const intensityColumnFormatter = intensityFormatter
    || ((cell) => defaultIntensityFormatter(cell, labels));
  const notificationColumnFormatter = notificationFormatter
    || ((cell) => defaultNotificationFormatter(cell, labels));
  return [
    {
      field: 'rowSelection',
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      hozAlign: 'center',
      width: 44,
      headerSort: false,
      resizable: false,
      headerFilter: false,
      frozen: true,
    },
    {
      field: 'editor',
      hozAlign: 'center',
      width: 44,
      headerSort: false,
      formatter: editRowFormatter,
      resizable: false,
      headerFilter: false,
      frozen: true,
    },
    buildTabulatorIdColumn({
      frozen: true,
      headerFilterFunc: idHeaderFilter,
    }),
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.name,
      field: 'name',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
      headerFilterFuncParams: { field: 'name' },
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.name),
      widthGrow: 1,
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.department_ids,
      field: 'department_ids',
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.department_ids, 'multiselect'),
      formatter: departmentFormatter || defaultDepartmentFormatter,
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { placeholder: uiT('Common.All'), valueType: 'string', options: departmentOptions },
      headerFilterFunc: departmentHeaderFilter,
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.users,
      field: 'users',
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.users, 'multiselect'),
      formatter: userFormatter || defaultUserFormatter,
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { placeholder: uiT('Common.All'), valueType: 'string', options: userOptions },
      headerFilterFunc: usersHeaderFilter,
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.sending_data,
      field: 'sending_data',
      formatter: intensityColumnFormatter,
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: intensityHeaderFilter,
      headerFilterFuncParams: { labels },
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.sending_data),
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.notification_before,
      field: 'notification_before',
      formatter: notificationColumnFormatter,
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: notificationBeforeHeaderFilter,
      headerFilterFuncParams: { labels },
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.notification_before),
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.manager_id,
      field: 'manager_id',
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.manager_id, 'multiselect'),
      formatter: managerFormatter || defaultManagerFormatter,
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { placeholder: uiT('Common.All'), valueType: 'string', options: managerOptions },
      headerFilterFunc: managerHeaderFilter,
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.manager_due_days,
      field: 'manager_due_days',
      headerFilter: 'number',
      headerFilterLiveFilter: false,
      headerFilterFunc: numberHeaderFilter,
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.manager_due_days),
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.description,
      field: 'description',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
      headerFilterFuncParams: { field: 'description' },
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.description),
      widthGrow: 1,
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES['machine.name'],
      field: 'machine.name',
      resizable: false,
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES['machine.name'], 'multiselect'),
      formatter: machineFormatter || defaultMachineFormatter,
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { placeholder: uiT('Common.All'), valueType: 'string', options: machineOptions },
      headerFilterFunc: machineNameHeaderFilter,
    },
    {
      title: CHECKLIST_TEMPLATE_COLUMN_TITLES.is_active,
      field: 'is_active',
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: {
        placeholder: uiT('Common.All'),
        valueType: 'string',
        options: CHECKLIST_TEMPLATE_IS_ACTIVE_OPTIONS,
      },
      headerFilterFunc: isActiveHeaderFilter,
      formatter: activeFormatter || defaultActiveFormatter,
      minWidth: tabulatorColumnMinWidthFromTitle(CHECKLIST_TEMPLATE_COLUMN_TITLES.is_active, 'multiselect'),
    },
    ...buildAuditTabulatorColumns({
      profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
      includeId: false,
      dateFormat,
      dateTimeFormat: dateTimeFmt,
      getUserColor,
      getUserInitials,
    }),
  ];
}
